import { ScreenStack, Snackbar, SnackbarOptions, TableRow } from "@mcleod/components";
import { ModelRow, PermissionsUtil, ActionPermission } from "@mcleod/core";
import { getDispatchControlValue } from "../models/ModelDispatchControl";
import { RowDispatchControlFields } from "../models/autogen/AutogenModelDispatchControl";

export interface PlanningTableRowActionProps {
    selectedRows: TableRow[];
}

export type SelectionValidation = {
    enabled: boolean;
    reasons: string[];
}

export type FieldNameOverrides = {
    orderStatusField: string;
    moveStatusField: string;
    billDateField: string;
    xfer2BillDateField: string;
    xfer2SettleDateField: string;
}

export abstract class PlanningTableRowAction {
    abstract execute(props: PlanningTableRowActionProps): void;
    abstract validateSelection(modelRows: ModelRow[]): boolean | SelectionValidation | Promise<SelectionValidation>;

    showInProgressSnackbar(detail: string, options?: SnackbarOptions) {
        return Snackbar.showDownloadSnackbar( "Multi-Select Action in Progress", detail, {
            persist: true,
            targetPanel: ScreenStack.getSnackbarTarget(),
            ...options
        });
    }

    validatePermissions(selectedRows: ModelRow[], isMoveEdit: boolean = true): boolean {
        return selectedRows.every(row =>
            this.hasUpdateOrderPermissions(row)
            && (!isMoveEdit ||  this.hasUpdateMovementPermissions(row))
        );
    }

    validateMultiSelect(selectedRows: ModelRow[], maxSelections: keyof RowDispatchControlFields | number, actionPermission?: ActionPermission): boolean {
        const isMultiSelect = selectedRows.length > 1;
        const isMultiSelectAllowed = actionPermission ? !PermissionsUtil.isUserDeniedAction(actionPermission) : true;
        const exceedsSelectionMax = selectedRows.length > (typeof maxSelections === "number" ? maxSelections : getDispatchControlValue(maxSelections));

        return (!isMultiSelect || isMultiSelectAllowed) && !exceedsSelectionMax;
    }

    public hasUpdateMovementPermissions(modelRow: ModelRow, fieldNameOverrides?: Partial<FieldNameOverrides>): boolean {
        const moveStatusField = fieldNameOverrides?.moveStatusField || "move_status";
        const xfer2SettleDateField = fieldNameOverrides?.xfer2SettleDateField || "xfer2settle_date";
        const billDateField = fieldNameOverrides?.billDateField || "orders.bill_date";

        const isNotDelivered = modelRow.getString(moveStatusField, null) !== "D"
        const isNotTransferred: boolean = modelRow.isNull(xfer2SettleDateField);
        const isNotBilled: boolean = modelRow.isNull(billDateField);

        const updateSettledAllowed: boolean = !PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_MOVE_AFTER_SETTLED);
        const updateDeliveredMoveAllowed = !PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_MOVES);

        return (isNotDelivered || updateDeliveredMoveAllowed)
                && ((isNotTransferred && isNotBilled) || updateSettledAllowed);
    }

    public hasUpdateOrderPermissions(modelRow: ModelRow, fieldNameOverrides?: Partial<FieldNameOverrides>): boolean {
        const orderStatusField = fieldNameOverrides?.orderStatusField || "order_status";
        const xfer2BillDateField = fieldNameOverrides?.xfer2BillDateField || "transfer_to_billing_date";

        const isNotDelivered = modelRow.getString(orderStatusField, null) !== "D";
        const isNotBilled = modelRow.isNull(xfer2BillDateField);

        const updateDeliveredAllowed = !PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_ORDER);
        const updateBilledAllowed = !PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_BILLED_ORDER);

        return (isNotDelivered || updateDeliveredAllowed)
                && (isNotBilled || updateBilledAllowed);
    }
}
